import { Component } from 'react';
import MoneySelector from './moneyselector.js';
import MembershipBenefitPanel from './membershipbenefitpanel.js';
import RecurringNudge from './recurringnudge.js';
import FrequencySelector, { FREQUENCY_OPTIONS } from './frequencyselector.js';
import NextButton from './nextbutton.js';
import { createValidator, required, min } from '../shared/validators.js';
import { trackEvent } from '../analytics/helpers.js';
import { getUrlParameter, isNullOrUndefined } from '../../shared/utils';
import { formatMoney } from './shared.js';

const DEFAULT_MONTHLY_VALUE = 15; // dollars
const DEFAULT_ANNUAL_VALUE = 50;
const DEFAULT_ONCE_VALUE = 100;
const MINIMUM_ONCE_DONATION_AMOUNT = 5;
const MINIMUM_RECURRING_DONATION_AMOUNT = 3;

const recurringValidator = createValidator({
  donationAmount: [required, min(MINIMUM_RECURRING_DONATION_AMOUNT)],
});

const onceValidator = createValidator({
  donationAmount: [required, min(MINIMUM_ONCE_DONATION_AMOUNT)],
});

export default class SupportIntent extends Component {
  constructor(props) {
    super(props);
    this.state = this.getInitialState(props);
  }

  getInitialState(props) {
    let amount =
      parseInt(getUrlParameter('amount'), 10) || props.savedDonationAmount;
    let urlPeriod = getUrlParameter('period') || props.savedDonationFrequency;

    if (isNaN(amount) || amount === 0) {
      amount = this.getDefaultAmount(urlPeriod);
    }

    if (amount < 0) {
      amount = Math.abs(amount);
    }

    if (isNullOrUndefined(FREQUENCY_OPTIONS[urlPeriod])) {
      urlPeriod = undefined;
    }

    return {
      donationAmount: amount,
      donationFrequency: urlPeriod || 'monthly',
      benefitDesired: true,
      amountSet: false,
      coverFees: props.coverFees,
    };
  }

  getDefaultAmount(frequency) {
    if (frequency === 'annually') {
      return DEFAULT_ANNUAL_VALUE;
    } else if (frequency === 'once') {
      return DEFAULT_ONCE_VALUE;
    } else {
      return DEFAULT_MONTHLY_VALUE;
    }
  }

  getRecommendedRecurringAmount() {
    return Math.max(5, Math.ceil(this.state.donationAmount / 12));
  }

  getTotesEnabled() {
    return this.props.enableTotes == 'true';
  }

  validate(properties) {
    const validator =
      properties.donationFrequency === FREQUENCY_OPTIONS.once
        ? onceValidator
        : recurringValidator;
    const errors = validator(properties);
    this.setState({ errors: errors });
    return errors;
  }

  valid() {
    return !this.state.errors || Object.keys(this.state.errors).length === 0;
  }

  updateAmount(amount) {
    const newState = Object.assign({}, this.state, {
      donationAmount: amount,
      amountSet: true,
    });
    this.setState(newState);
    this.validate(newState);
  }

  updateFrequency(frequency, cb = undefined) {
    this.setState((state, props) => {
      const updatedState = {
        donationFrequency: frequency,
      };

      if (!state.amountSet) {
        updatedState.donationAmount = this.getDefaultAmount(frequency);
      }

      return updatedState;
    }, cb);
  }

  handleNudgeAgreement() {
    const amount = this.getRecommendedRecurringAmount();
    trackEvent('checkout', 'agreed-recurring', '', amount);
    this.updateAmount(amount);
    this.updateFrequency(FREQUENCY_OPTIONS.monthly, () => this.handleNext());
  }

  handleNudgeDecline() {
    trackEvent('checkout', 'decline-recurring', '', this.state.donationAmount);
    this.handleNext();
  }

  handleCoverFeesChange(event) {
    this.setState({ coverFees: event.target.checked });
  }

  toggleBenefitDesired() {
    this.setState({
      benefitDesired: !this.state.benefitDesired,
    });
  }

  handleNext() {
    const errors = this.validate(this.state);
    if (this.valid()) {
      this.props.onComplete(this.state);
    }
  }

  coverFeesOption() {
    return (
      <div className="cover-fees-container">
        <label className="minimal-checkbox">
          <input
            type="checkbox"
            className="hidden-checkbox"
            checked={this.state.coverFees}
            onChange={this.handleCoverFeesChange.bind(this)}
            name="coverFees"
          />
          <span className="fake-checkbox">
            <svg viewBox="0 0 55.36 55.36">
              <polyline
                className="subscribe-checkmark"
                points="6.29,27.21 23.16,45.21 51.29,9.21 "
              />
            </svg>
          </span>
          <span className="cover-fees-option-text">
            I'd like to cover all transaction fees so 100% of my donation goes
            to The Marshall Project.
          </span>
        </label>
      </div>
    );
  }

  render() {
    let className = '';
    if (!this.props.active && this.props.complete) {
      className += ' support-section-complete';
    }

    return (
      <div className={`support-intent ${className}`}>
        {this.props.active && this.renderActive()}
        {!this.props.active && this.props.complete && this.renderComplete()}
      </div>
    );
  }

  renderActive() {
    return (
      <div>
        <span className="support-intent-sentence">
          Yes, I will contribute{' '}
          <MoneySelector
            value={this.state.donationAmount}
            frequency={this.state.donationFrequency}
            errors={this.state.errors}
            onChange={this.updateAmount.bind(this)}
          />
          <FrequencySelector
            value={this.state.donationFrequency}
            onChange={this.updateFrequency.bind(this)}
          />
        </span>
        {this.coverFeesOption()}
        {this.getTotesEnabled() && (
          <MembershipBenefitPanel
            frequency={this.state.donationFrequency}
            amount={this.state.donationAmount}
            benefitDesired={this.state.benefitDesired}
            toggleBenefitDesired={this.toggleBenefitDesired.bind(this)}
          />
        )}
        {this.renderCTA()}
        {this.getTotesEnabled() && (
          <div className="tote-container">
            <img
              className="tote-image"
              src="https://d63kb4t2ifcex.cloudfront.net/upload/assets/tmpbagfinal.cc60ea84.gif"
            />
          </div>
        )}
      </div>
    );
  }

  renderComplete() {
    return (
      <div>
        <h2>Contribution</h2>
        <span className="support-section-summaries">
          Contributing ${formatMoney(this.props.savedDonationAmount)}{' '}
          {this.props.savedDonationFrequency}.
        </span>
      </div>
    );
  }

  renderCTA() {
    if (
      this.state.donationFrequency == FREQUENCY_OPTIONS.once &&
      this.valid()
    ) {
      return (
        <RecurringNudge
          amount={this.state.donationAmount}
          recurringAmount={this.getRecommendedRecurringAmount()}
          frequency={this.state.donationFrequency}
          onDecline={this.handleNudgeDecline.bind(this)}
          onAgree={this.handleNudgeAgreement.bind(this)}
        />
      );
    } else if (!this.valid()) {
      return (
        <p>
          <span className="error-message">
            We do not accept less than ${MINIMUM_RECURRING_DONATION_AMOUNT} for
            recurring donations or less than ${MINIMUM_ONCE_DONATION_AMOUNT} for
            one-time donations.
          </span>
        </p>
      );
    } else {
      return (
        <NextButton
          onClick={this.handleNext.bind(this)}
          text={this.props.complete ? 'Update' : 'Next'}
        />
      );
    }
  }
}
